import React, { useRef, useState } from "react";
import { CalendarClock, ExternalLink, Printer, Upload } from "lucide-react";
import { toast } from "sonner";
import { InvoiceExpected } from "@/components/nds/windows/finance/ExpectedPaychecks";
import { finApi, finErr, money } from "@/lib/financeApi";
import { btnGold, when } from "@/components/nds/windows/finance/parts";
import InvoiceActions, { useIsSuper } from "@/components/nds/windows/finance/InvoiceActions";
import { PaymentsList, ReceivePaymentForm } from "@/components/nds/windows/finance/InvoicePayments";
import { ResidentBreakdown } from "@/components/nds/windows/finance/ResidentBreakdown";

const Field = ({ label, children, testid }) => (
  <div className="space-y-0.5">
    <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{label}</div>
    <div data-testid={testid} className="text-sm text-nex-silver break-words">{children ?? "—"}</div>
  </div>
);
const who = (a, t) => (a ? `${a.name} · ${when(t)}` : null);

export default function InvoiceDetailsTab({ inv, reload, onDeleted }) {
  const isSuper = useIsSuper();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try { await finApi.upload(`/invoices/${inv.id}/document`, file); toast.success("Document attached"); reload(); }
    catch (err) { toast.error(finErr(err)); } finally { setBusy(false); }
  };
  const open = () => finApi.openFile(inv.document_url).catch((e) => toast.error(finErr(e)));
  const [pdfBusy, setPdfBusy] = useState(false);
  const pdf = () => {
    setPdfBusy(true);
    finApi.download(`/invoices/${inv.id}/pdf`, {}, `Invoice-${(inv.invoice_number || inv.id.slice(0, 8)).replace(/[/"]/g, "-")}.pdf`)
      .catch((e) => toast.error(finErr(e))).finally(() => setPdfBusy(false));
  };

  return (
    <div data-testid="fin-inv-details-tab" className="p-5 space-y-5">
      <InvoiceExpected inv={inv} />
      <div className="flex flex-wrap items-center gap-6 p-4 border border-nex-gold/40 rounded-sm bg-nex-gold/[0.04]">
        <CalendarClock className="h-6 w-6 text-nex-gold" />
        <div>
          <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">{inv.legacy_import ? (inv.status === "paid" ? (inv.payments?.filter((p) => !p.reversed).slice(-1)[0]?.received_on ? "Paid on · employer payment received" : "Paid on · old platform") : "Pay date · old platform") : `Pay date${inv.self_reported ? " · self-reported" : " · +3 business days"}`}</div>
          <div data-testid="fin-inv-detail-paydate" className="font-mono text-2xl text-nex-gold">{inv.pay_date}</div>
        </div>
        <div className="ml-auto grid grid-cols-3 gap-6 text-right">
          <Field label="Total" testid="fin-inv-detail-total">{money(inv.total_amount)}{inv.test_lines_hidden > 0 && <span data-testid="fin-inv-test-lines-note" className="ml-1.5 text-[10px] text-fuchsia-300">includes {inv.test_lines_hidden} test line{inv.test_lines_hidden === 1 ? "" : "s"} hidden</span>}</Field>
          <Field label="Paid" testid="fin-inv-detail-paid">{money(inv.paid_amount)}</Field>
          {inv.balance_due < 0
            ? <Field label="Margin · overhead retained" testid="fin-inv-detail-margin">{money(-inv.balance_due)}</Field>
            : <Field label="Balance due" testid="fin-inv-detail-balance">{money(inv.balance_due)}</Field>}
        </div>
        <button type="button" onClick={pdf} disabled={pdfBusy} data-testid="fin-inv-pdf" className={`${btnGold} inline-flex items-center gap-1.5`}>
          <Printer className="h-3.5 w-3.5" /> {pdfBusy ? "Making PDF…" : "Print / Download PDF"}</button>
      </div>
      <InvoiceActions inv={inv} onChanged={reload} onDeleted={onDeleted} />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Field label="Vendor">{inv.vendor_name} ({inv.vendor_type})</Field>
        <Field label="Invoice #">{inv.invoice_number}</Field>
        <Field label="Invoice date">{inv.invoice_date}</Field>
        <Field label="Period">{inv.period_start} → {inv.period_end}</Field>
        <Field label="Linked residents">{inv.resident_ids?.length ? `${inv.resident_ids.length} resident(s)` : "Org-level"}</Field>
        <Field label="Created by">{who(inv.created_by, inv.created_at)}</Field>
        <Field label="Submitted">{who(inv.submitted_by, inv.submitted_at)}</Field>
        <Field label="Approved">{who(inv.approved_by, inv.approved_at)}</Field>
        <Field label="Last paid">{who(inv.paid_by, inv.paid_at)}</Field>
        {inv.status === "voided" && <Field label="Void reason" testid="fin-inv-detail-void-reason">{inv.void_reason}</Field>}
        {inv.flow && <Field label="Direction" testid="fin-inv-detail-flow">{inv.flow === "receivable" ? "Billed by TEA to this employer" : "Rent billed to TEA by the landlord"}</Field>}
        {inv.cycle && <Field label="Cycle">{inv.cycle}</Field>}
        {inv.memo && <Field label="Memo">{inv.memo}</Field>}
      </div>
      <ResidentBreakdown rows={inv.resident_breakdown} />
      {inv.legacy_import && (
        <div data-testid="fin-inv-legacy-note" className="space-y-2 p-3 border border-white/10 rounded-sm bg-white/[0.02]">
          <p className="text-[11px] text-nex-muted">Imported from the old platform · the original PDF is attached and no resident ledger rows are posted.</p>
          {isSuper && inv.flow === "receivable" && inv.status === "approved" && <ReceivePaymentForm inv={inv} onDone={reload} />}
          <PaymentsList inv={inv} onDone={reload} />
          {inv.balance_due < 0 && <p data-testid="fin-inv-margin-note" className="text-[11px] text-nex-muted">Margin {money(-inv.balance_due)} · the amount received above the invoice total is overhead retained (insurance, workers' comp) and stays in the deposit to ...1403.</p>}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-white/5">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-nex-muted">Document</span>
        {inv.document_url ? (
          <button type="button" onClick={open} data-testid="fin-inv-document-link" className="inline-flex items-center gap-1.5 text-sm text-nex-gold hover:underline">
            {inv.document_name || "Open document"} <ExternalLink className="h-3 w-3" />
          </button>
        ) : <span data-testid="fin-inv-document-none" className="text-sm text-nex-muted">None attached</span>}
        {inv.status !== "voided" && !inv.legacy_import && (
          <>
            <input ref={fileRef} type="file" accept=".pdf,image/*" onChange={upload} className="hidden" data-testid="fin-inv-document-input" />
            <button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="fin-inv-document-upload" className={`${btnGold} inline-flex items-center gap-1.5`}>
              <Upload className="h-3 w-3" /> {busy ? "Uploading…" : inv.document_url ? "Replace" : "Upload"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
